// useGlobalUserSearch.js
// Debounced GlobalUser search. Returns full profile data including pfphref,
// user_type, creator_username so RecipientCard can render avatars + profile links.
// Skips the API call when the query is already a valid email or phone.

import { useState, useEffect } from "react";
import { isValidEmail, isValidPhone } from "./T2a.utils";
import client from "../../../API";

/**
 * Result shape returned from the search endpoint:
 * {
 *   global_user_id : number,
 *   creator        : string,        // creator username (if creator account)
 *   display_name   : string,
 *   email          : string | null,
 *   phone_number   : string | null,
 *   pfphref        : string | null, // avatar URL
 *   user_type      : string,        // "creator" | "user" | etc.
 *   creator_username: string | null,
 *   balance        : string | null, // serialised Decimal — omit from UI
 * }
 */

export function useGlobalUserSearch(query, debounceMs = 300) {
  const [results,         setResults]         = useState([]);
  const [isSearching,     setIsSearching]     = useState(false);
  const [hasSearched,     setHasSearched]     = useState(false);

  const q               = (query || "").trim();
  const isDirectContact = isValidEmail(q) || isValidPhone(q);

  useEffect(() => {
    if (q.length < 2 || isDirectContact) {
      setResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      setHasSearched(false);
      try {
        const response = await client.globalUsers.search({ q, limit: 10 });

        // Normalise response — API may return { results } or { users }
        const raw = response?.results?.length > 0
          ? response.results
          : (response?.users || []).map((u) => ({
              global_user_id:   u.id,
              creator:          u.username,
              display_name:     u.name || u.display_name || u.username || u.email,
              email:            u.email || null,
              phone_number:     u.phone_number || null,
              pfphref:          u.pfphref || u.profile_photo || null,
              user_type:        u.user_type || "user",
              creator_username: u.creator_username || null,
            }));

        setResults(raw);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [q, isDirectContact, debounceMs]);

  return { results, isSearching, hasSearched, isDirectContact };
}